// Battles and battle reports (04 §3.5, §5.7) and battle events (§7.4).
import { z } from 'zod';
import {
  idSchema,
  isoDateTimeSchema,
  resourcesSchema,
  shipCountsSchema,
  shipTypeSchema,
} from './common.js';

export const BATTLE_STATUSES = ['active', 'attacker_won', 'defender_won'] as const;
export const BATTLE_END_REASONS = [
  'defender_eliminated',
  'attacker_eliminated',
  'mutual_destruction',
  'attacker_retreated',
  'no_defenders',
] as const;
export const battleStatusSchema = z.enum(BATTLE_STATUSES);
export const battleEndReasonSchema = z.enum(BATTLE_END_REASONS);
export const battleOutcomeSchema = z.enum(['attacker_won', 'defender_won']);
export const battleSideSchema = z.enum(['attacker', 'defender']);

const count = z.number().int().nonnegative();

export const battleParticipantViewSchema = z.strictObject({
  fleetId: idSchema,
  playerId: idSchema.nullable(),
  displayName: z.string().nullable(),
  joinedBeforeRound: z.number().int().positive(),
  retreating: z.boolean(),
  leftAfterRound: z.number().int().nullable(),
  leftReason: z.string().nullable(),
});

const sideViewFields = {
  stacks: z.partialRecord(shipTypeSchema, z.strictObject({ count, carriedDamage: count })),
  participants: z.array(battleParticipantViewSchema),
};

/** Involved players and their alliance (04 §3.5): both sides, participants, rounds so far. */
export const liveBattleSchema = z.strictObject({
  view: z.literal('full'),
  id: idSchema,
  systemId: idSchema,
  systemName: z.string(),
  status: battleStatusSchema,
  endReason: battleEndReasonSchema.nullable(),
  /** uint32 battle seed as 8 hex digits (A-2). */
  seed: z.string().regex(/^[0-9a-f]{8}$/),
  roundIntervalS: z.number().int().positive(),
  roundsRun: count,
  nextRoundAt: isoDateTimeSchema.nullable(),
  startedAt: isoDateTimeSchema,
  endedAt: isoDateTimeSchema.nullable(),
  sides: z.strictObject({
    attacker: z.strictObject(sideViewFields),
    defender: z.strictObject({ ...sideViewFields, odpLevel: count }),
  }),
  waitingFleets: count,
});
export type LiveBattle = z.infer<typeof liveBattleSchema>;

/** A waiting attacker (G-36): that the battle runs, since when, rounds so far, own place in the queue. */
export const waitingBattleSchema = z.strictObject({
  view: z.literal('waiting'),
  id: idSchema,
  systemId: idSchema,
  status: battleStatusSchema,
  startedAt: isoDateTimeSchema,
  roundsRun: count,
  queuePosition: z.number().int().positive(),
});
export type WaitingBattle = z.infer<typeof waitingBattleSchema>;

export const battleViewSchema = z.discriminatedUnion('view', [
  liveBattleSchema,
  waitingBattleSchema,
]);
export type BattleView = z.infer<typeof battleViewSchema>;

/** One stack in one round: what the core received and what it returned (enough to replay). */
export const roundStackSchema = z.strictObject({
  shipType: shipTypeSchema,
  countBefore: count,
  firingCount: count,
  carriedDamageBefore: count,
  damageReceived: count,
  /** Damage this stack dealt (M3, the capture tie-break G-24); rounds stored before M3 lack it. */
  damageDealt: count.optional(),
  destroyed: count,
  carriedDamageAfter: count,
});
export type RoundStack = z.infer<typeof roundStackSchema>;

export const battleRoundSchema = z.strictObject({
  round: z.number().int().positive(),
  dueAt: isoDateTimeSchema,
  ranAt: isoDateTimeSchema,
  roundSeed: z.number().int().nonnegative(),
  joined: z.array(idSchema),
  retreating: z.array(idSchema),
  stacks: z.strictObject({
    attacker: z.array(roundStackSchema),
    defender: z.array(roundStackSchema),
  }),
  odpLevel: count,
  odpDamage: count,
});
export type BattleRound = z.infer<typeof battleRoundSchema>;

export const battleRoundsResponseSchema = z.strictObject({ items: z.array(battleRoundSchema) });

export const battleListItemSchema = z.strictObject({
  id: idSchema,
  systemId: idSchema,
  systemName: z.string(),
  status: battleStatusSchema,
  startedAt: isoDateTimeSchema,
  endedAt: isoDateTimeSchema.nullable(),
  roundsRun: count,
  role: z.enum(['attacker', 'defender', 'waiting']),
});
export const battleListResponseSchema = z.strictObject({ items: z.array(battleListItemSchema) });
export type BattleListResponse = z.infer<typeof battleListResponseSchema>;

export const retreatResponseSchema = z.strictObject({
  fleetIds: z.array(idSchema),
  effectiveRound: z.number().int().positive(),
});
export type RetreatResponse = z.infer<typeof retreatResponseSchema>;

export const battleReportListItemSchema = z.strictObject({
  battleId: idSchema,
  systemId: idSchema,
  systemName: z.string(),
  startedAt: isoDateTimeSchema,
  endedAt: isoDateTimeSchema.nullable(),
  outcome: battleOutcomeSchema,
  role: battleSideSchema,
  viewed: z.boolean(),
});
export const battleReportListResponseSchema = z.strictObject({
  items: z.array(battleReportListItemSchema),
});
export type BattleReportListResponse = z.infer<typeof battleReportListResponseSchema>;

export const reportParticipantSchema = z.strictObject({
  fleetId: idSchema,
  playerId: idSchema.nullable(),
  displayName: z.string().nullable(),
  side: battleSideSchema,
  joinedBeforeRound: z.number().int().positive(),
  shipsAtJoin: shipCountsSchema,
  losses: shipCountsSchema,
  /** Metal + Gas + Supplies the lost ships cost. */
  lossValue: count,
  loot: resourcesSchema,
  leftAfterRound: z.number().int().nullable(),
  leftReason: z.string().nullable(),
});

const reportPlayerSchema = z.strictObject({ playerId: idSchema, displayName: z.string() });

export const battleReportSchema = z.strictObject({
  battleId: idSchema,
  system: z.strictObject({ id: idSchema, name: z.string() }),
  attacker: reportPlayerSchema,
  /** Null for an unowned system (no pirates before M3). */
  defender: reportPlayerSchema.nullable(),
  startedAt: isoDateTimeSchema,
  endedAt: isoDateTimeSchema.nullable(),
  outcome: battleOutcomeSchema,
  endReason: battleEndReasonSchema,
  systemCaptured: z.boolean(),
  capturedBy: idSchema.nullable(),
  forces: z.strictObject({
    attacker: shipCountsSchema,
    defender: shipCountsSchema,
    odpLevel: count,
  }),
  rounds: z.array(battleRoundSchema),
  participants: z.array(reportParticipantSchema),
  seed: z.string().regex(/^[0-9a-f]{8}$/),
});
export type BattleReport = z.infer<typeof battleReportSchema>;

export const battleStartedSchema = z.strictObject({
  battleId: idSchema,
  systemId: idSchema,
  role: battleSideSchema,
  nextRoundAt: isoDateTimeSchema.nullable(),
});

export const battleRoundResolvedSchema = z.strictObject({
  battleId: idSchema,
  round: z.number().int().positive(),
  stacks: battleRoundSchema.shape.stacks,
  odpDamage: count,
  nextRoundAt: isoDateTimeSchema.nullable(),
});

export const battleFleetJoinedSchema = z.strictObject({
  battleId: idSchema,
  fleetId: idSchema,
  side: battleSideSchema,
  beforeRound: z.number().int().positive(),
  ships: shipCountsSchema,
});

export const battleRetreatOrderedSchema = z.strictObject({
  battleId: idSchema,
  playerId: idSchema,
  effectiveRound: z.number().int().positive(),
});

export const battleEndedSchema = z.strictObject({
  battleId: idSchema,
  outcome: battleOutcomeSchema,
  endReason: battleEndReasonSchema,
  systemCaptured: z.boolean(),
  reportId: idSchema,
});

export const battleWaitingChangedSchema = z.strictObject({
  battleId: idSchema,
  queuePosition: z.number().int().positive(),
  roundsRun: count,
});
